import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {BasePalette} from '#core/application/modules/base-palette.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateStakeInput} from '#core/application/modules/tiles/stake-input/index.js';
import {CHECKBOX_TYPES, generateCheckbox} from '#core/application/modules/tiles/checkbox/index.js';
import {BET_FUNDS_INFO_TYPES, generateBetFundsInfo} from '#core/application/modules/tiles/bet-funds-info/index.js';
import {generateAnimatedButton} from '#core/application/modules/tiles/button/index.js';
import {generateBetReceipt} from '#core/application/modules/tiles/bet-receipt/index.js';
import {generateSecondaryLink} from '#core/application/modules/tiles/link/index.js';
import {generatePrimaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {
    generateErrorNotification,
    generateInfoNotification,
    generateWarningNotification,
} from '#core/application/modules/tiles/notification/index.js';
import {generateOddsBoostBanner} from '#core/application/modules/tiles/odds-boost-banner/index.js';

export function quickBetslipFactory(PALETTE) {
    const BET_BASE_INFO = generateBetBaseInfo(PALETTE.QUICK_BETSLIP_BET_BASE_INFO);
    const BET_STAKE_INPUT = merge(generateStakeInput(PALETTE.QUICK_BETSLIP_BET_STAKE_INPUT), {
        checkbox: merge(generateCheckbox(PALETTE.QUICK_BETSLIP_BET_EACH_WAY_CHECKBOX, CHECKBOX_TYPES.VERTICAL), {
            container: {
                style: {
                    base: {
                        flexDirection: 'row-reverse',
                        margin: 0,
                    },
                },
            },
        }),
    });
    const PLACE_BET_TOTAL_INFO_NORMAL = generateBetFundsInfo(PALETTE.QUICK_BETSLIP_PLACE_BET_TOTAL_INFO);
    const PLACE_BET_TOTAL_INFO_EMPHASIZED = generateBetFundsInfo(
        PALETTE.QUICK_BETSLIP_PLACE_BET_TOTAL_INFO,
        BET_FUNDS_INFO_TYPES.EMPHASIZED
    );

    return {
        QuickBetslipView: {
            container: {
                createStackingContext: true,
                style: {
                    ...resolveBackground(PALETTE.QUICK_BETSLIP_CONTAINER_BG),
                    boxShadow: PALETTE.QUICK_BETSLIP_CONTAINER_SHADOW,
                    padding: '28px 0 env(safe-area-inset-bottom, 0px) 0',
                },
            },
            disabledOverlay: {
                style: {
                    ...resolveBackground(PALETTE.QUICK_BETSLIP_LOADING_OVERLAY_BG),
                    top: 0,
                },
            },
            betslipNotifications: {
                error: generateErrorNotification(PALETTE.QUICK_BETSLIP_ERROR_NOTIFICATION),
                warning: generateWarningNotification(PALETTE.QUICK_BETSLIP_WARNING_NOTIFICATION),
                info: generateInfoNotification(PALETTE.QUICK_BETSLIP_INFO_NOTIFICATION),
            },
            accumulatorBet: {
                container: {
                    style: {
                        padding: '0 0 8px 0',
                    },
                },
                label: {
                    style: {
                        ...BET_BASE_INFO.selectionName,
                    },
                },
                selection: {
                    style: {
                        ...PALETTE.QUICK_BETSLIP_ACCUMULATOR_BET_MARKET_NAME_TEXT,
                    },
                },
                oddsContainer: {
                    itemSpacing: 4,
                },
                odds: {
                    updatedPrice: {
                        style: {
                            ...BET_BASE_INFO.oddsChangedValue.newValueContainer.style,
                        },
                    },
                    priceContainer: {
                        style: {
                            ...BET_BASE_INFO.odds,
                        },
                    },
                    priceBoostIndicator: BET_BASE_INFO.priceBoostValue,
                    bestOddsIcon: BET_BASE_INFO.bestOddsIcon,
                },
                stakeInput: BET_STAKE_INPUT,
                selectionMoreButton: {
                    style: {
                        base: {
                            ...PALETTE.QUICK_BETSLIP_SELECTION_MORE_BUTTON_TEXT,
                        },
                    },
                },
                earlyPayoutIndicator: BET_BASE_INFO.earlyPayoutIndicator,
                betBonusPromotions: {
                    bonusIndicator: BET_BASE_INFO.betBonusIndicator,
                },
                oddsBoostBanner: generateOddsBoostBanner(PALETTE.QUICK_BETSLIP_ODDS_BOOST_BANNER),
                removeSelection: {
                    removeButton: {
                        imageButton: {
                            innerContainer: {
                                style: {
                                    padding: `${Math.max(8 + BasePalette.palette.VERTICAL_ALIGN_FIX, 0)}px 0 0`,
                                },
                            },
                            buttonIcon: icon(PALETTE.QUICK_BETSLIP_REMOVE_BET_BUTTON.CLOSE_BUTTON_ICON_COLOR, 'cross'),
                            button: {
                                style: {
                                    base: {
                                        padding: 3,
                                        height: 'auto',
                                        width: 26,
                                    },
                                },
                            },
                        },
                    },
                },
            },
            singleBet: {
                betContent: {
                    default: {
                        selectionName: {
                            truncate: true,
                            style: {
                                ...BET_BASE_INFO.selectionName,
                            },
                        },
                        selectionNameSuspended: {
                            truncate: true,
                            style: BET_BASE_INFO.selectionName,
                        },
                        marketName: {
                            style: BET_BASE_INFO.marketName,
                        },
                        marketNameSuspended: {
                            style: BET_BASE_INFO.marketName,
                        },
                        eventName: {
                            style: BET_BASE_INFO.eventName,
                        },
                        eventNameSuspended: {
                            style: BET_BASE_INFO.eventName,
                        },
                        earlyPayoutIndicator: BET_BASE_INFO.earlyPayoutIndicator,
                        removeSelection: {
                            removeButton: {
                                imageButton: {
                                    innerContainer: {
                                        style: {
                                            padding: `${Math.max(8 + BasePalette.palette.VERTICAL_ALIGN_FIX, 0)}px 0 0`,
                                        },
                                    },
                                    buttonIcon: icon(
                                        PALETTE.QUICK_BETSLIP_REMOVE_BET_BUTTON.CLOSE_BUTTON_ICON_COLOR,
                                        'cross'
                                    ),
                                    button: {
                                        style: {
                                            base: {
                                                padding: 3,
                                                height: 'auto',
                                                width: 26,
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        odds: {
                            // temp solution, will be redesigned in next versions
                            updatedPrice: {
                                style: {
                                    ...BET_BASE_INFO.oddsChangedValue.newValueContainer.style,
                                },
                            },
                            priceContainer: {
                                style: {
                                    ...BET_BASE_INFO.odds,
                                },
                            },
                            priceBoostIndicator: BET_BASE_INFO.priceBoostValue,
                            bestOddsIcon: BET_BASE_INFO.bestOddsIcon,
                        },
                        betslipBet: {
                            stakeInfoContainer: {
                                itemSpacing: 4,
                                style: {
                                    justifyContent: 'initial',
                                },
                            },
                            betInfoContainer: {
                                style: {
                                    ...resolveBackground(PALETTE.QUICK_BETSLIP_BET_CONTAINER_BG),
                                },
                            },
                        },
                        stakeInput: BET_STAKE_INPUT,
                        multicastSelectionInfoContainer: {
                            style: {
                                padding: 0,
                            },
                        },
                        multicastSelectionName: {
                            style: {
                                ...BET_BASE_INFO.selectionName,
                            },
                        },
                        multicastMarketName: {
                            container: {
                                padding: '4px 0',
                            },
                            style: {
                                ...BET_BASE_INFO.marketName,
                            },
                        },
                        multicastEventName: {
                            style: {
                                ...BET_BASE_INFO.eventName,
                            },
                        },
                        multicastAllOddsButton: generateSecondaryLink(PALETTE.QUICK_BETSLIP_BET_SHOW_ODDS_BUTTON),
                        marketSwitchDropdown: generatePrimaryDropdown(PALETTE.QUICK_BETSLIP_MARKETS_DROPDOWN),
                    },
                    matchAcca: {
                        stakeInput: BET_STAKE_INPUT,
                    },
                },
                oddsBoostBanner: generateOddsBoostBanner(PALETTE.QUICK_BETSLIP_ODDS_BOOST_BANNER),
                betBonusPromotions: {
                    bonusIndicator: BET_BASE_INFO.betBonusIndicator,
                },
            },
            // place bet total info
            placeBetCalculations: {
                calculationsContainer: {
                    style: {
                        ...resolveBackground(PALETTE.QUICK_BETSLIP_PLACE_BET_TOTAL_INFO_CONTAINER_BG),
                        borderStyle: 'none',
                        _mobile_padding: '24px 8px 8px',
                    },
                },
                totalPayable: {
                    betInfo: PLACE_BET_TOTAL_INFO_EMPHASIZED,
                },
                prewagerStake: {
                    cash: PLACE_BET_TOTAL_INFO_NORMAL,
                    bonus: PLACE_BET_TOTAL_INFO_NORMAL,
                },
                freebetStake: {
                    betInfo: PLACE_BET_TOTAL_INFO_NORMAL,
                },
                stakeTax: {
                    betInfo: PLACE_BET_TOTAL_INFO_NORMAL,
                },
                totalPayoutInfo: {
                    payout: {
                        betInfo: PLACE_BET_TOTAL_INFO_EMPHASIZED,
                    },
                    prewager: {
                        cash: PLACE_BET_TOTAL_INFO_NORMAL,
                        bonus: PLACE_BET_TOTAL_INFO_NORMAL,
                    },
                    tax: {
                        betInfo: PLACE_BET_TOTAL_INFO_NORMAL,
                    },
                    payoutAfterTax: {
                        betInfo: PLACE_BET_TOTAL_INFO_EMPHASIZED,
                    },
                },
                oddsBoostInfo: {
                    betInfo: PLACE_BET_TOTAL_INFO_NORMAL,
                },
            },
            stakeInput: BET_STAKE_INPUT,
            placeBetButton: {
                buttonStyle: generateAnimatedButton(PALETTE.QUICK_BETSLIP_PLACE_BET_BUTTON),
                totalStakeText: {
                    style: {
                        ...PALETTE.QUICK_BETSLIP_PLACE_BET_BUTTON_TOTAL_STAKE_TEXT,
                    },
                },
                potentialReturns: {
                    style: {
                        lineHeight: 1,
                        ...PALETTE.QUICK_BETSLIP_PLACE_BET_BUTTON_POTENTIAL_RETURNS_TEXT,
                    },
                },
                potentialReturnValue: {
                    style: {
                        lineHeight: 1,
                        ...PALETTE.QUICK_BETSLIP_PLACE_BET_BUTTON_POTENTIAL_RETURNS_TEXT,
                    },
                },
            },
            // receipt
            betReceipt: {
                placedBetReceiptView: merge(
                    generateBetReceipt({
                        ...PALETTE.QUICK_BETSLIP_BET_RECEIPT,
                        BET_RECEIPT_CONTAINER_BG: PALETTE.QUICK_BETSLIP_BET_RECEIPT_BG,
                    }),
                    {
                        headerIcon: {
                            src: '',
                        },
                        hideDownloadPdfButton: ApplicationConfig.hideDownloadPdfButton,
                        betReceiptHeader: {
                            style: {
                                backgroundImage: 'transparent',
                                backgroundColor: 'transparent',
                                borderColor: 'transparent',
                                padding: '0 0 0 8px',
                            },
                        },
                        betReceiptHeaderSuccessText: {
                            style: {
                                ...PALETTE.QUICK_BETSLIP_BET_RECEIPT_PLACED_BET_TITLE_TEXT,
                            },
                        },
                        betReceiptHeaderDateText: {
                            style: {
                                ...PALETTE.QUICK_BETSLIP_BET_RECEIPT_PLACED_BET_DATE_TEXT,
                            },
                        },
                        betView: {
                            legsContainer: {
                                style: {
                                    padding: '0 16px',
                                },
                            },
                        },
                        betReceiptBodyContainer: {
                            separator: {
                                style: {
                                    margin: '8px 0 8px',
                                },
                            },
                        },
                        totalFundsContainer: {
                            style: {
                                padding: '0 8px',
                            },
                        },
                    }
                ),
            },
        },
    };
}
